import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as freezeRecordApi from '../../api/freezeRecord';
import { message } from 'antd';

// 获取账户冻结记录列表
export const fetchFreezeRecords = createAsyncThunk(
  'freezeRecord/fetchFreezeRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await freezeRecordApi.getAccountFreezeRecordList(params);
      if (response && response.code === 200) {
    
        return {
          data: response.data,
          params: params
        };
      }
      return rejectWithValue(response?.message || '获取账户冻结记录列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取账户冻结记录列表失败');
    }
  }
);

// 获取陪逛员余额冻结记录列表
export const fetchProviderBalanceFreezeRecords = createAsyncThunk(
  'freezeRecord/fetchProviderBalanceFreezeRecords',
  async (data, { rejectWithValue }) => {
    try {
      const response = await freezeRecordApi.getProviderBalanceFreezeRecordPage(data);
      if (response && response.code === 200) {
        return {
          data: response.data,
          params: data
        };
      }
      return rejectWithValue(response?.message || '获取陪逛员余额冻结记录列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取陪逛员余额冻结记录列表失败');
    }
  }
);

// 获取商家余额冻结记录列表
export const fetchMerchantBalanceFreezeRecords = createAsyncThunk(
  'freezeRecord/fetchMerchantBalanceFreezeRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await freezeRecordApi.getMerchantBalanceFreezeRecordPage(params);
      if (response && response.code === 200) {
        return {
          data: response.data,
          params
        };
      }
      return rejectWithValue(response?.message || '获取商家余额冻结记录列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取商家余额冻结记录列表失败');
    }
  }
);

// 获取用户余额冻结记录列表
export const fetchUserBalanceFreezeRecords = createAsyncThunk(
  'freezeRecord/fetchUserBalanceFreezeRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await freezeRecordApi.getUserBalanceFreezeRecordPage(params);
      if (response && response.code === 200) {
        return {
          data: response.data,
          params
        };
      }
      return rejectWithValue(response?.message || '获取用户余额冻结记录列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取用户余额冻结记录列表失败');
    }
  }
);

// 初始状态
const initialState = {
  freezeRecordList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      userId: undefined,
      accountId: undefined,
      freezeNo: '',
      freezeType: undefined,
      freezeStatus: undefined,
      minAmount: undefined,
      maxAmount: undefined,
      businessType: '',
      businessId: '',
      orderNo: '',
      username: '',
      nickname: '',
      startTime: undefined,
      endTime: undefined
    }
  },
  userBalanceFreezeRecordList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      userId: undefined,
      orderId: undefined,
      status: undefined
    }
  },
  providerBalanceFreezeRecordList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      providerId: undefined,
      orderNo: '',
      status: undefined,
      minAmount: undefined,
      maxAmount: undefined,
      startTime: undefined,
      endTime: undefined
    }
  },
  merchantBalanceFreezeRecordList: {
    data: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null,
    searchParams: {
      merchantId: undefined,
      orderNo: '',
      status: undefined,
      minAmount: undefined,
      maxAmount: undefined,
      startTime: undefined,
      endTime: undefined
    }
  }
};

// 创建slice
const freezeRecordSlice = createSlice({
  name: 'freezeRecord',
  initialState,
  reducers: {
    setSearchParams: (state, action) => {
      state.freezeRecordList.searchParams = {
        ...state.freezeRecordList.searchParams,
        ...action.payload
      };
    },
    resetSearchParams: (state) => {
      state.freezeRecordList.searchParams = {
        userId: undefined,
        accountId: undefined,
        freezeNo: '',
        freezeType: undefined,
        freezeStatus: undefined,
        minAmount: undefined,
        maxAmount: undefined,
        businessType: '',
        businessId: '',
        orderNo: '',
        username: '',
        nickname: '',
        startTime: undefined,
        endTime: undefined
      };
    },
    setUserSearchParams: (state, action) => {
      state.userBalanceFreezeRecordList.searchParams = {
        ...state.userBalanceFreezeRecordList.searchParams,
        ...action.payload
      };
    },
    resetUserSearchParams: (state) => {
      state.userBalanceFreezeRecordList.searchParams = {
        userId: undefined,
        orderId: undefined,
        status: undefined
      };
    },
    setProviderSearchParams: (state, action) => {
      state.providerBalanceFreezeRecordList.searchParams = {
        ...state.providerBalanceFreezeRecordList.searchParams,
        ...action.payload
      };
    },
    resetProviderSearchParams: (state) => {
      state.providerBalanceFreezeRecordList.searchParams = {
        providerId: undefined,
        orderNo: '',
        status: undefined,
        minAmount: undefined,
        maxAmount: undefined,
        startTime: undefined,
        endTime: undefined
      };
    },
    setMerchantSearchParams: (state, action) => {
      state.merchantBalanceFreezeRecordList.searchParams = {
        ...state.merchantBalanceFreezeRecordList.searchParams,
        ...action.payload
      };
    },
    resetMerchantSearchParams: (state) => {
      state.merchantBalanceFreezeRecordList.searchParams = {
        merchantId: undefined,
        orderNo: '',
        status: undefined,
        minAmount: undefined,
        maxAmount: undefined,
        startTime: undefined,
        endTime: undefined
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取账户冻结记录列表
      .addCase(fetchFreezeRecords.pending, (state) => {
        state.freezeRecordList.loading = true;
        state.freezeRecordList.error = null;
      })
      .addCase(fetchFreezeRecords.fulfilled, (state, action) => {
        state.freezeRecordList.loading = false;
        state.freezeRecordList.data = action.payload.data.records || [];
        state.freezeRecordList.total = action.payload.data.total || 0;
        
        // 使用传入的page和size参数更新状态，而不是依赖后端返回的值
        if (action.payload.params) {
          if (action.payload.params.page) {
            state.freezeRecordList.current = action.payload.params.page;
          } else {
            state.freezeRecordList.current = action.payload.data.current || 1;
          }
          
          if (action.payload.params.size) {
            state.freezeRecordList.size = action.payload.params.size;
          } else {
            state.freezeRecordList.size = action.payload.data.size || 10;
          }
        } else {
          state.freezeRecordList.current = action.payload.data.current || 1;
          state.freezeRecordList.size = action.payload.data.size || 10;
        }
        
        // 检查是否有明确传入的搜索参数，如果有则更新searchParams
        if (action.payload.params) {
          // 过滤掉page和size参数，避免将它们保存到searchParams中
          const { page, size, ...searchParams } = action.payload.params;
          
          // 如果没有传入特定的搜索参数，则不要覆盖现有的searchParams
          const hasSearchParams = Object.values(searchParams).some(value => 
            value !== undefined && value !== '' && value !== null
          );
          
          // 只有在有明确的搜索参数时才更新
          if (hasSearchParams) {
            state.freezeRecordList.searchParams = {
              ...state.freezeRecordList.searchParams,
              ...searchParams
            };
          }
        }
      })
      .addCase(fetchFreezeRecords.rejected, (state, action) => {
        state.freezeRecordList.loading = false;
        state.freezeRecordList.error = action.payload;
      })
      // 处理获取陪逛员余额冻结记录列表
      .addCase(fetchProviderBalanceFreezeRecords.pending, (state) => {
        state.providerBalanceFreezeRecordList.loading = true;
        state.providerBalanceFreezeRecordList.error = null;
      })
      .addCase(fetchProviderBalanceFreezeRecords.fulfilled, (state, action) => {
        state.providerBalanceFreezeRecordList.loading = false;
        state.providerBalanceFreezeRecordList.data = action.payload.data.records || [];
        state.providerBalanceFreezeRecordList.total = action.payload.data.total || 0;
        
        // 使用传入的page和size参数更新状态
        if (action.payload.params) {
          if (action.payload.params.page) {
            state.providerBalanceFreezeRecordList.current = action.payload.params.page;
          } else {
            state.providerBalanceFreezeRecordList.current = action.payload.data.current || 1;
          }
          
          if (action.payload.params.size) {
            state.providerBalanceFreezeRecordList.size = action.payload.params.size;
          } else {
            state.providerBalanceFreezeRecordList.size = action.payload.data.size || 10;
          }
        } else {
          state.providerBalanceFreezeRecordList.current = action.payload.data.current || 1;
          state.providerBalanceFreezeRecordList.size = action.payload.data.size || 10;
        }
        
        // 检查是否有明确传入的搜索参数，如果有则更新searchParams
        if (action.payload.params) {
          // 过滤掉page和size参数，避免将它们保存到searchParams中
          const { page, size, ...searchParams } = action.payload.params;
          
          // 如果没有传入特定的搜索参数，则不要覆盖现有的searchParams
          const hasSearchParams = Object.values(searchParams).some(value => 
            value !== undefined && value !== '' && value !== null
          );
          
          // 只有在有明确的搜索参数时才更新
          if (hasSearchParams) {
            state.providerBalanceFreezeRecordList.searchParams = {
              ...state.providerBalanceFreezeRecordList.searchParams,
              ...searchParams
            };
          }
        }
      })
      .addCase(fetchProviderBalanceFreezeRecords.rejected, (state, action) => {
        state.providerBalanceFreezeRecordList.loading = false;
        state.providerBalanceFreezeRecordList.error = action.payload;
      })
      // 处理获取用户余额冻结记录列表
      .addCase(fetchUserBalanceFreezeRecords.pending, (state) => {
        state.userBalanceFreezeRecordList.loading = true;
        state.userBalanceFreezeRecordList.error = null;
      })
      .addCase(fetchUserBalanceFreezeRecords.fulfilled, (state, action) => {
        state.userBalanceFreezeRecordList.loading = false;
        state.userBalanceFreezeRecordList.data = action.payload.data.records || [];
        state.userBalanceFreezeRecordList.total = action.payload.data.total || 0;
        if (action.payload.params) {
          state.userBalanceFreezeRecordList.current = action.payload.params.page || action.payload.data.current || 1;
          state.userBalanceFreezeRecordList.size = action.payload.params.size || action.payload.data.size || 10;
        } else {
          state.userBalanceFreezeRecordList.current = action.payload.data.current || 1;
          state.userBalanceFreezeRecordList.size = action.payload.data.size || 10;
        }
        if (action.payload.params) {
          const { page, size, ...searchParams } = action.payload.params;
          const hasSearchParams = Object.values(searchParams).some(value => 
            value !== undefined && value !== '' && value !== null
          );
          if (hasSearchParams) {
            state.userBalanceFreezeRecordList.searchParams = {
              ...state.userBalanceFreezeRecordList.searchParams,
              ...searchParams
            };
          }
        }
      })
      .addCase(fetchUserBalanceFreezeRecords.rejected, (state, action) => {
        state.userBalanceFreezeRecordList.loading = false;
        state.userBalanceFreezeRecordList.error = action.payload;
      })
      // 处理获取商家余额冻结记录列表
      .addCase(fetchMerchantBalanceFreezeRecords.pending, (state) => {
        state.merchantBalanceFreezeRecordList.loading = true;
        state.merchantBalanceFreezeRecordList.error = null;
      })
      .addCase(fetchMerchantBalanceFreezeRecords.fulfilled, (state, action) => {
        state.merchantBalanceFreezeRecordList.loading = false;
        state.merchantBalanceFreezeRecordList.data = action.payload.data.records || [];
        state.merchantBalanceFreezeRecordList.total = action.payload.data.total || 0;
        if (action.payload.params) {
          state.merchantBalanceFreezeRecordList.current = action.payload.params.page || action.payload.data.current || 1;
          state.merchantBalanceFreezeRecordList.size = action.payload.params.size || action.payload.data.size || 10;
        } else {
          state.merchantBalanceFreezeRecordList.current = action.payload.data.current || 1;
          state.merchantBalanceFreezeRecordList.size = action.payload.data.size || 10;
        }
        if (action.payload.params) {
          const { page, size, ...searchParams } = action.payload.params;
          const hasSearchParams = Object.values(searchParams).some(value =>
            value !== undefined && value !== '' && value !== null
          );
          if (hasSearchParams) {
            state.merchantBalanceFreezeRecordList.searchParams = {
              ...state.merchantBalanceFreezeRecordList.searchParams,
              ...searchParams
            };
          }
        }
      })
      .addCase(fetchMerchantBalanceFreezeRecords.rejected, (state, action) => {
        state.merchantBalanceFreezeRecordList.loading = false;
        state.merchantBalanceFreezeRecordList.error = action.payload;
      });
  }
});

export const { setSearchParams, resetSearchParams, setUserSearchParams, resetUserSearchParams, setProviderSearchParams, resetProviderSearchParams, setMerchantSearchParams, resetMerchantSearchParams } = freezeRecordSlice.actions;

export default freezeRecordSlice.reducer; 